import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  CommercialClientTermResponse,
  CommercialClientTermSuggestedSignatoryResponse,
  LegatusTermSignatoryResponse,
  TermSignerVerificationResponse,
} from '@interfaces/partners/responses/commercial-client-term.response';
import { AddCommercialClientTermSignatoryRequest } from '@interfaces/partners/requests/commercial-client-term.request';
import { PartnerDealResponse } from '@interfaces/partners/responses/partner-deal.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultMaskCnpjPipe } from '@shared/pipes/vault-mask-cnpj.pipe';
import { finalize, first } from 'rxjs';

/**
 * Celular brasileiro com DDD: 10 ou 11 dígitos, aceitando DDI 55 e máscara (card 3073). Vazio
 * passa — "obrigatório" é do `required`, que só entra quando a verificação é por SMS/WhatsApp.
 *
 * @param control Campo do celular.
 */
export function celularComDdd(control: AbstractControl): ValidationErrors | null {
  let digitos = ((control.value as string) ?? '').replace(/\D/g, '');
  if (!digitos) return null;
  if ((digitos.length === 12 || digitos.length === 13) && digitos.startsWith('55')) {
    digitos = digitos.slice(2);
  }
  return digitos.length === 10 || digitos.length === 11 ? null : { celular: true };
}

/**
 * Exige nome **e** sobrenome.
 *
 * 🐞 Nasceu de um 422 do provedor: `{"errors":["Nome Completo : utilize nome e sobrenome"]}`.
 * O Clicksign recusa signatário com uma palavra só — e recusava tarde, depois de o documento já
 * ter sido criado lá. O resultado era um documento órfão no provedor e o termo travado aqui.
 *
 * A regra é do provedor, então ela precisa valer **antes** de qualquer chamada: barrar na modal
 * é o que evita o lixo do outro lado.
 *
 * @param control Campo do nome.
 */
export function nomeESobrenome(control: AbstractControl): ValidationErrors | null {
  const valor = ((control.value as string) ?? '').trim();
  if (!valor) return null; // "obrigatório" é problema do `required`, não deste validador.

  const partes = valor.split(/\s+/);

  // O último pedaço precisa ser um sobrenome de verdade, e não uma inicial ou partícula solta:
  // "Breno M" e "Breno de" passariam numa contagem simples de palavras.
  return partes.length >= 2 && partes[partes.length - 1].length >= 2 ? null : { sobrenome: true };
}

/**
 * Escolhe quem assina o NDA + carta de autorização de SCR pelo cliente (card 2952, bloco D).
 *
 * Serve aos dois momentos, porque a pergunta é a mesma: **quem assina?**
 * - termo ainda não existe → envia para assinatura;
 * - termo já aberto → acrescenta assinante à lista.
 *
 * ⚠️ **A Receita não fornece e-mail nem CPF dos sócios** — só nome e qualificação. Como o
 * provedor de assinatura identifica e convida a pessoa pelo e-mail, a sugestão diz *quem* deve
 * assinar e alguém digita o endereço. Não existe "marcar os sócios e enviar", e a modal precisa
 * dizer isso: senão a pessoa procura um botão que não pode existir.
 *
 * ⚠️ **Acrescentar assinante a um termo já enviado invalida as assinaturas já colhidas.** O
 * provedor cancela o documento e abre outro, em branco, e todos assinam de novo. Por isso a
 * confirmação explícita antes de salvar nesse caso — quem clica precisa saber que vai derrubar a
 * assinatura de quem já assinou.
 */
@Component({
  selector: 'app-modal-term-signatories',
  templateUrl: './modal-term-signatories.component.html',
  styleUrls: ['./modal-term-signatories.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule, VaultMaskCnpjPipe],
})
export class ModalTermSignatoriesComponent extends VaultBaseComponent implements OnDestroy {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Avisa a tela do deal para recarregar o termo. */
  @Output() salvou = new EventEmitter<void>();

  /** Deal em contexto — exibido no cabeçalho para a pessoa conferir. */
  deal: PartnerDealResponse | null = null;

  /** Termo atual. `null` significa "ainda não enviado", e muda o que a modal faz ao salvar. */
  termo: CommercialClientTermResponse | null = null;

  /** Sócios vindos da Receita. Vazio é normal e não impede nada. */
  sugestoes: CommercialClientTermSuggestedSignatoryResponse[] = [];

  carregandoSugestoes = false;

  /**
   * Em que passo a modal está.
   *
   * O envio é em dois tempos de propósito: escolher quem assina é uma decisão, e conferir o
   * documento que vai sair é outra. Juntar as duas num clique só faz a pessoa confirmar um
   * documento que ela nunca viu.
   */
  passo: 'assinantes' | 'conferencia' = 'assinantes';

  /** Quem assina pela Legatus — só leitura, vem da Parametrização. */
  assinantesDaLegatus: LegatusTermSignatoryResponse[] = [];

  carregandoLegatus = false;
  carregandoPreview = false;

  /**
   * Como o signatário do cliente confirma a identidade no Clicksign (card 3073). `null` enquanto
   * carrega, ou se a consulta falhar — aí vale o padrão (e-mail), e o backend recusa o envio se a
   * configuração exigir celular.
   */
  verificacao: TermSignerVerificationResponse | null = null;

  /** Celular obrigatório: a assinatura é confirmada por SMS ou WhatsApp. */
  get exigeCelular(): boolean {
    return !!this.verificacao?.requiresPhone;
  }

  /** Frase que diz ao parceiro o que o cliente vai precisar fazer para assinar. */
  get descricaoDaVerificacao(): string | null {
    const v = this.verificacao;
    if (!v) return null;

    const canal =
      v.auth === 'sms'
        ? 'um código enviado por SMS'
        : v.auth === 'whatsapp'
          ? 'um código enviado por WhatsApp'
          : 'um código enviado por e-mail';
    const extras = [
      v.selfie ? 'uma selfie' : null,
      v.officialDocument ? 'a foto de um documento oficial' : null,
      v.handwritten ? 'a assinatura desenhada na tela' : null,
    ].filter(Boolean);

    return extras.length
      ? `Para assinar, o cliente confirma com ${canal} e envia ${extras.join(', ')}.`
      : `Para assinar, o cliente confirma com ${canal}.`;
  }

  /** O documento montado, pronto para o `iframe`. */
  preview: SafeResourceUrl | null = null;

  nomeDoArquivo = '';

  /** A URL crua, guardada só para poder ser revogada depois. */
  private urlDoPreview: string | null = null;

  /** Ver o comentário homônimo na modal de valor: não use `isLoading`. */
  gravando = false;

  /**
   * Uma linha por pessoa que vai assinar.
   *
   * O nome pode vir da Receita (e aí fica travado) ou ser digitado. O **e-mail é sempre
   * digitado**, venha o nome de onde vier.
   */
  form: FormGroup = this.formBuilder.group({
    assinantes: this.formBuilder.array([]),
  });

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private partnersService: PartnersHttpService,
    private toast: ToastHelperService,
    private confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  get assinantes(): FormArray<FormGroup> {
    return this.form.get('assinantes') as FormArray<FormGroup>;
  }

  /**
   * `true` quando o termo já existe **no provedor** — muda o texto e obriga a confirmação.
   *
   * ⚠️ `Pendente` conta como **não enviado**. Esse status só existe entre gravar a linha e abrir
   * o processo no Clicksign; se a abertura falhou (provedor fora do ar, gerador de PDF parado),
   * o termo fica ali sem documento nenhum. Tratá-lo como enviado levava a pessoa para "acrescentar
   * signatário" — que avisa que vai cancelar um documento que nunca existiu.
   */
  get termoJaEnviado(): boolean {
    const status = this.termo?.status;
    return !!status && status !== 'Pendente' && status !== 'Cancelado';
  }

  get titulo(): string {
    if (this.passo === 'conferencia') return 'Confira o termo antes de enviar';

    return this.termoJaEnviado ? 'Acrescentar signatário' : 'Solicitar assinatura do cliente';
  }

  /** "1 signatário" / "3 signatários" — o contador da seção 2. */
  get contagemDeSignatarios(): string {
    const total = this.assinantes.length;
    return total === 1 ? '1 signatário' : `${total} signatários`;
  }

  /**
   * Quantos signatários estão sem sobrenome — a tela usa para explicar o botão travado.
   *
   * Sem isto, o botão desabilitado não diz por quê, e a pessoa fica procurando o que faltou.
   */
  get semSobrenome(): number {
    return this.assinantes.controls.filter(c => c.get('name')?.hasError('sobrenome')).length;
  }

  /** Fecha sem salvar. */
  fechar(): void {
    this.dialogRef?.close();
  }

  /** O botão final. O passo 1 apenas confirma a escolha; quem envia é o passo 2. */
  get rotuloDoBotao(): string {
    return this.termoJaEnviado ? 'Confirmar e acrescentar' : 'Confirmar envio';
  }

  /** Abre a modal e busca os sócios e a contraparte da Legatus. */
  abrir(deal: PartnerDealResponse, termo: CommercialClientTermResponse | null): void {
    this.deal = deal;
    this.termo = termo;
    this.sugestoes = [];
    this.assinantesDaLegatus = [];
    this.passo = 'assinantes';
    this.descartarPreview();
    this.assinantes.clear();

    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'term-signatories', false, true);

    this.carregarSugestoes(deal.id);
    this.carregarAssinantesDaLegatus(deal.id);
    this.carregarVerificacao(deal.id);
  }

  /**
   * Busca a verificação configurada (card 3073) e ajusta o celular de cada linha: obrigatório por
   * SMS/WhatsApp, opcional por e-mail. As linhas podem já existir quando a resposta chega.
   */
  private carregarVerificacao(dealId: string): void {
    this.verificacao = null;

    this.partnersService
      .getDealTermSignerVerification(dealId)
      .pipe(first())
      .subscribe({
        next: v => {
          this.verificacao = v;
          this.assinantes.controls.forEach(linha => this.aplicarRegraDoCelular(linha));
          this.cdr.markForCheck();
        },
        error: () => (this.verificacao = null),
      });
  }

  /** Celular: obrigatório (com DDD) quando a verificação pede; senão, só o formato. */
  private aplicarRegraDoCelular(linha: FormGroup): void {
    const celular = linha.get('phoneNumber');
    if (!celular) return;

    celular.setValidators(
      this.exigeCelular ? [Validators.required, celularComDdd] : [celularComDdd]
    );
    celular.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Busca quem assina pela Legatus.
   *
   * Lista vazia **não** é detalhe: sem assinante da Legatus o envio é recusado, e o documento
   * nunca fecharia. A seção 3 diz isso na hora em que a pessoa está escolhendo, em vez de
   * deixar a recusa para depois de todo o trabalho.
   */
  private carregarAssinantesDaLegatus(dealId: string): void {
    this.carregandoLegatus = true;
    this.cdr.markForCheck();

    this.partnersService
      .getDealTermLegatusSignatories(dealId)
      .pipe(
        first(),
        finalize(() => {
          this.carregandoLegatus = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: lista => (this.assinantesDaLegatus = lista ?? []),
        error: () => (this.assinantesDaLegatus = []),
      });
  }

  /**
   * Busca os sócios do CNPJ na Receita.
   *
   * Falha aqui **não** vira erro na tela: a sugestão é conveniência, e a digitação manual é o
   * caminho principal. O backend já devolve lista vazia quando a consulta não responde.
   */
  private carregarSugestoes(dealId: string): void {
    this.carregandoSugestoes = true;
    this.cdr.markForCheck();

    this.partnersService
      .getDealTermSuggestedSignatories(dealId)
      .pipe(first())
      .subscribe({
        next: lista => {
          this.sugestoes = lista ?? [];
          this.carregandoSugestoes = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.sugestoes = [];
          this.carregandoSugestoes = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** Acrescenta uma linha em branco, para quem não é sócio. */
  acrescentarLinha(nome = '', qualificacao: string | null = null, daReceita = false): void {
    this.assinantes.push(
      this.formBuilder.group({
        name: [nome, [Validators.required, Validators.minLength(3), nomeESobrenome]],
        email: ['', [Validators.required, Validators.email]],
        phoneNumber: [''],
        qualification: [qualificacao],
        fromCorporateRegistry: [daReceita],
      })
    );
    this.aplicarRegraDoCelular(this.assinantes.at(this.assinantes.length - 1));
    this.cdr.markForCheck();
  }

  /**
   * Traz um sócio do quadro para a lista de signatários.
   *
   * ⚠️ **Não alterna.** Uma vez escolhido, o botão vira rótulo e quem desfaz é a lixeira da
   * seção 2 — dois caminhos para desfazer a mesma coisa é como se erra sem perceber.
   */
  selecionar(socio: CommercialClientTermSuggestedSignatoryResponse): void {
    if (socio.alreadyAdded || this.estaSelecionado(socio.name)) return;

    // Termo já aberto acrescenta UM por vez: a chamada do backend recebe um signatário, e cada
    // acréscimo recria o documento no provedor.
    if (this.termoJaEnviado) this.assinantes.clear();

    this.acrescentarLinha(socio.name, socio.qualification ?? null, true);
  }

  /** Acrescenta uma linha de nome livre, para quem não consta no quadro societário. */
  adicionarForaDoQuadro(): void {
    if (this.termoJaEnviado) this.assinantes.clear();

    this.acrescentarLinha();
  }

  /** Diz se o sócio já foi escolhido — o botão vira rótulo "Selecionado". */
  estaSelecionado(nome: string): boolean {
    const alvo = (nome ?? '').trim().toUpperCase();
    return this.assinantes.controls.some(
      c => ((c.get('name')?.value as string) ?? '').trim().toUpperCase() === alvo
    );
  }

  removerLinha(indice: number): void {
    this.assinantes.removeAt(indice);
    this.cdr.markForCheck();
  }

  isFormValid(): boolean {
    return this.form.valid && this.assinantes.length > 0 && !!this.deal;
  }

  // ──────────────────── passo 2: conferir o documento ────────────────────

  /**
   * Compõe o documento com os assinantes escolhidos e passa para a conferência.
   *
   * ⚠️ O preview vem do **mesmo caminho** que o envio usaria, então uma recusa aqui é a mesma
   * que apareceria depois do "Confirmar envio". É esse o ganho do passo: o impedimento aparece
   * ANTES de a pessoa dizer sim, e não depois.
   */
  confirmarAssinantes(): void {
    if (!this.isFormValid() || this.carregandoPreview) return;

    this.carregandoPreview = true;
    this.cdr.markForCheck();

    this.partnersService
      .previewDealTerm(this.deal!.id, this.montarPayload())
      .pipe(
        first(),
        finalize(() => {
          this.carregandoPreview = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: preview => {
          if (!preview?.contentBase64) {
            this.toast.showToastError('Não foi possível montar o documento.');
            return;
          }

          this.mostrarPreview(preview.contentBase64);
          this.nomeDoArquivo = preview.fileName;
          this.passo = 'conferencia';
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível montar o documento',
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          ),
      });
  }

  /** Volta para a escolha de assinantes, descartando o documento montado. */
  voltarParaAssinantes(): void {
    this.passo = 'assinantes';
    this.descartarPreview();
    this.cdr.markForCheck();
  }

  /**
   * Monta a URL local do PDF para o `iframe`.
   *
   * O conteúdo vem em base64 e vira um `blob:` — que não passa pela rede de novo e não precisa
   * de token. O `bypassSecurityTrustResourceUrl` é necessário porque o Angular bloqueia `blob:`
   * em `[src]` de iframe por padrão; a origem aqui é a própria resposta da nossa API, não
   * conteúdo de terceiro.
   */
  private mostrarPreview(base64: string): void {
    this.descartarPreview();

    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });

    this.urlDoPreview = URL.createObjectURL(blob);
    this.preview = this.sanitizer.bypassSecurityTrustResourceUrl(this.urlDoPreview);
  }

  /** Libera o `blob:`. Sem isto o PDF fica na memória da aba até ela ser fechada. */
  private descartarPreview(): void {
    if (this.urlDoPreview) URL.revokeObjectURL(this.urlDoPreview);
    this.urlDoPreview = null;
    this.preview = null;
  }

  /** A base já usa `ngOnDestroy` para encerrar as inscrições — daí o `super`. */
  override ngOnDestroy(): void {
    this.descartarPreview();
    super.ngOnDestroy();
  }

  submitAction(): void {
    if (!this.isFormValid() || this.gravando) return;

    // ⚠️ Acrescentar a um termo aberto CANCELA o documento no provedor e abre outro, em branco.
    // Quem já assinou assina de novo. Isso não pode acontecer sem a pessoa saber.
    if (this.termoJaEnviado) {
      const jaAssinou = (this.termo?.signatories ?? []).some(s => !!s.signedAt);
      const aviso = jaAssinou
        ? 'O documento atual será cancelado e um novo será enviado. As assinaturas já coletadas ' +
          'serão perdidas, e todos precisarão assinar de novo. Deseja continuar?'
        : 'O documento atual será cancelado e um novo será enviado a todos os assinantes. ' +
          'Deseja continuar?';

      this.confirmationService
        .confirm('Acrescentar assinante', aviso)
        .pipe(first())
        .subscribe(confirmado => {
          if (confirmado) this.gravar();
        });

      return;
    }

    this.gravar();
  }

  /** Os assinantes escolhidos, normalizados — o mesmo corpo vai ao preview e ao envio. */
  private montarPayload(): AddCommercialClientTermSignatoryRequest[] {
    const linhas = this.assinantes.getRawValue() as AddCommercialClientTermSignatoryRequest[];

    return linhas.map(l => ({
      name: (l.name ?? '').trim(),
      email: (l.email ?? '').trim().toLowerCase(),
      phoneNumber: (l.phoneNumber ?? '').trim() || null,
      qualification: l.qualification || null,
      fromCorporateRegistry: !!l.fromCorporateRegistry,
    }));
  }

  private gravar(): void {
    const payload = this.montarPayload();

    this.gravando = true;
    this.cdr.markForCheck();

    const chamada = this.termoJaEnviado
      ? this.partnersService.addDealTermSignatory(this.deal!.id, payload[0])
      : this.partnersService.requestDealTermSignature(this.deal!.id, payload);

    chamada.pipe(first()).subscribe({
      next: () => {
        this.gravando = false;
        this.cdr.markForCheck();
        this.toast.showToastSuccess(
          this.termoJaEnviado ? 'Assinante acrescentado' : 'Termo enviado para assinatura',
          ''
        );
        this.dialogRef?.close();
        this.salvou.emit();
      },
      error: error => {
        this.gravando = false;
        this.cdr.markForCheck();
        // A mensagem do backend é a que diz o que fazer ("sem assinante da Legatus
        // configurado", "e-mail repetido"). O texto genérico só cobre falha técnica.
        this.toast.showToastError(
          'Não foi possível concluir',
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }
}
